// API
import BreedingManagementAPI from "@/app/apis/main/breeding-management/breeding-management-api.js";

export default {
    
    async getBreederDamAndSireList(queryParams) {
		return BreedingManagementAPI.getBreederDamAndSireList(queryParams)
	},

	async getMatingList(queryParams) {
		return BreedingManagementAPI.getMatingList(queryParams)
	},

	async deleteMatingDetails(id) {
		return BreedingManagementAPI.deleteMatingDetails(id)
	},
	/**
	 * Retrieves a list of breeders based on the specified gender.
	 * 
	 * @param {("Dam" | "Sire")} gender - The gender of the breeders to retrieve. It can be either "Dam" or "Sire".
	 * @returns {Promise<any>} A promise that resolves to a list of breeders matching the specified gender.
	 */
	async getBreederDamAndSireListByGender(gender) {
		return BreedingManagementAPI.getBreederDamAndSireListByGender(gender);
	},
	
	/**
	 * Fetches the mating details of a Dam or Sire by ID by sending a GET request.
	 *
	 * @async
	 * @function getDamAndSireMatingDetails
	 * @param {string} identifier - Specifies whether to retrieve details for a "dam" or "sire".
	 * @param {number} detailsId - The ID of the Dam or Sire for which to fetch mating details.
	 * @returns {Promise<Object>} The response from the API containing the Dam/Sire mating details.
	 * @throws {Error} If the API request fails.
	 */
    async getDamAndSireMatingDetails(identifier, matingId) {
		return BreedingManagementAPI.getDamAndSireMatingDetails(identifier,matingId)
	},

	/**
	 * Submits mating details for a dam and sire to the Breeding Management API.
	 * 
	 * @param {Object} matingDetails - The details of the mating to be submitted.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after submitting the mating details.
	 */
	async addDamAndSireMatingDetails(matingDetails) {
		return BreedingManagementAPI.addDamAndSireMatingDetails(matingDetails);
	},

	/**
	 * Updates mating details for a specific mating ID using the Breeding Management API.
	 * 
	 * @param {number} matingId - The ID of the mating to be updated.
	 * @param {Object} matingDetails - The updated details of the mating.
	 * @param {number | null} matingDetails.damId - The ID of the dam. `null` if not applicable.
	 * @param {number | null} matingDetails.sireId - The ID of the sire. `null` if not applicable.
	 * @param {string} matingDetails.note - Additional notes about the mating.
	 * @returns {Promise<any>} A promise that resolves to the API response after updating the mating details.
	 */
	async updateDamAndSireMatingDetails(matingId, matingDetails) {
		return BreedingManagementAPI.updateDamAndSireMatingDetails(matingId, matingDetails);
	},

	// *********************************** Ovulation / Gestation ****************************************************
	/**
	 * Saves ovulation and gestation details for a dam in the Breeding Management system.
	 *
	 * @async
	 * @function saveOvulationAndGestation
	 * @param {Object} details - The ovulation and gestation details to be saved.
	 * @param {number} details.damId - The ID of the dam.
	 * @param {string} details.matingDate - The mating date of the dam (in ISO 8601 format).
	 * @param {string} details.ovulationStartDate - The ovulation start date of the dam (in ISO 8601 format).
	 * @returns {Promise<Object>} A promise that resolves to the response from the Breeding Management API.
	 */
	async saveOvulationAndGestation(details) {
		return BreedingManagementAPI.saveOvulationAndGestation(details);
	},

	/**
	 * Retrieves ovulation and gestation details for a specific dam.
	 *
	 * @async
	 * @function getDamOvulationAndGestationDetails
	 * @param {number} damId - The ID of the dam whose ovulation and gestation details are to be retrieved.
	 * @param {number} matingId - The ID of the mating whose cycle dates are to be retrieved.
	 * @returns {Promise<Object>} A promise that resolves to the response from the API containing the ovulation and gestation details.
	 */
	async getDamOvulationAndGestationDetails(damId, matingId) {
		return BreedingManagementAPI.getDamOvulationAndGestationDetails(damId, matingId);
	},

	/**
	 * Sends a request to save the details of a litter.
	 * 
	 * @param {Object} details - The details of the litter to be saved.
	 * @param {number|null} [details.breedId] - The ID of the breed. If no breed, use `null`.
	 * @param {number} details.matingId - The ID of the mating.
	 * @param {string} details.name - The name of the litter.
	 * @param {string} [details.status] - The status of the litter. Example: `LITTER_STATUS.ACTIVE`. Optional.
	 * @param {string|null} [details.dateOfBirth] - The date of birth of the litter in ISO format. If not provided, use `null`.
	 * @param {string} details.identifyingMarks - Identifying marks of the litter.
	 * @param {string} details.collarColour - The colour of the collar.
	 * @param {number} details.birthWeight - The birth weight of the litter.
	 * @param {string} details.unitsOfWeight - The units of weight. Always "kg".
	 * @param {string} details.gender - The gender of the litter.
	 * @param {number} details.breederSysId - The system ID of the breeder.
	 * @param {string} [details.id] - Optional ID of the litter.
	 * @param {string} [details.status] - Optional status of the litter.
	 * 
	 * @returns {Promise<Object>} The response from the API.
	 */
  
	async saveLittersDetails(details, littersImageUploads) {
		return BreedingManagementAPI.saveLittersDetails(details, littersImageUploads);
	},
  
 	async getPuppyManagementList(queryParams) {
		return BreedingManagementAPI.getPuppyManagementList(queryParams)
	},

	async getLitterList(id, queryParams) {
		return BreedingManagementAPI.getLitterList(id, queryParams)
	},

	/**
	 * Deletes the litter details associated with the specified ID using the Breeding Management API.
	 *
	 * This function calls the `deleteLitterDetails` method of the `BreedingManagementAPI`
	 * to remove the litter details for the given ID.
	 *
	 * @async
	 * @function deleteLitterDetails
	 * @param {number|string} id - The ID of the litter details to be deleted.
	 * @returns {Promise<Object>} A promise that resolves to the response from the API.
	 *
	 */
	async deleteLitterDetails(id) {
		return BreedingManagementAPI.deleteLitterDetails(id)
	},

	/**
	 * Asynchronously retrieves the details of the dam and sire for a given litter.
	 *
	 * @async
	 * @function getDamAndSireLitterDetails
	 * @param {string} litterId - The unique identifier of the litter for which details are being requested.
	 * @returns {Promise<Object>} A promise that resolves to an object containing the details of the dam and sire.
	 * 
	 * @throws {Error} Throws an error if the API call fails or if the litterId is invalid.
	 */
	async getDamAndSireLitterDetails(litterId) {
		return BreedingManagementAPI.getDamAndSireLitterDetails(litterId)
	},

	/**
	 * Asynchronously saves the details of puppies for a specific litter.
	 *
	 * This function delegates the saving of puppy details to the BreedingManagementAPI.
	 *
	 * @async
	 * @function saveLitterPuppyDetails
	 * @param {string} litterId - The unique identifier of the litter for which puppy details are being saved.
	 * @param {Object} details - An object containing the details of the puppies to be saved.
	 * @returns {Promise<Object>} A promise that resolves to the response from the API, indicating the result of the save operation.
	 *
	 * @throws {Error} Throws an error if the API call fails or if the provided litterId or details are invalid.
	 *
	 * 
	 */
	async saveLitterPuppyDetails(litterId, details) {
		return BreedingManagementAPI.saveLitterPuppyDetails(litterId, details);
	},

	async getDamDiaryDetails(damId) {
		return BreedingManagementAPI.getDamDiaryDetails(damId)
	},

	// *********************************** Mating / Ovulation / Gestation / Whelping ****************************************************
	/**
	 * Saves mating and ovulation/gestation/whelping details for a dam and sire.
	 *
	 * @async
	 * @function saveMatingAndOvulationGestationWhelping
	 * @param {Object} details - The details to be saved.
	 * @param {Object} details.matingDetails - Mating information for the dam and sire.
	 * @param {number|null} details.matingDetails.damId - ID of the dam, or `null` if not selected.
	 * @param {number|null} details.matingDetails.sireId - ID of the sire, or `null` if not selected.
	 * @param {string|null} details.matingDetails.note - Additional note related to the mating details.
	 * @param {Object} details.cycleDetails - Cycle-related details for the dam.
	 * @param {number} details.cycleDetails.damId - ID of the dam.
	 * @param {string|null} details.cycleDetails.ovulationStartDate - The ovulation start date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @param {string|null} details.cycleDetails.matingDate - The mating date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @returns {Promise<Object>} The response from the API.
	 */
	async saveMatingAndOvulationGestationWhelping(details) {
		return BreedingManagementAPI.saveMatingAndOvulationGestationWhelping(details);
	},

	/**
	 * Updates mating and ovulation/gestation/whelping details for a dam and sire.
	 *
	 * @async
	 * @function updateMatingAndOvulationGestationWhelping
	 * @param {Object} details - The details to be updated.
	 * @param {Object} details.matingDetails - Mating information for the dam and sire.
	 * @param {number|null} details.matingDetails.damId - ID of the dam, or `null` if not selected.
	 * @param {number|null} details.matingDetails.sireId - ID of the sire, or `null` if not selected.
	 * @param {string|null} details.matingDetails.note - Additional note related to the mating details.
	 * @param {Object} details.cycleDetails - Cycle-related details for the dam.
	 * @param {number} details.cycleDetails.damId - ID of the dam.
	 * @param {string|null} details.cycleDetails.ovulationStartDate - The ovulation start date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @param {string|null} details.cycleDetails.matingDate - The mating date in 'yyyy-MM-dd' format, or `null` if not provided.
	 * @returns {Promise<Object>} The response from the API.
	 */
	async updateMatingAndOvulationGestationWhelping(details) {
		return BreedingManagementAPI.updateMatingAndOvulationGestationWhelping(details);
	},

	/**
 	* Updates the mating status.
 	* 
	* @param {number} matingId - The ID of the mating.
 	* @param {Object} details - The details of the mating status update.
 	* @param {string} details.status - The status of the mating (successful or unsuccessful).
 	* @returns {Promise} - A promise that resolves when the mating status is updated.
 	*/
	async updateMatingStatus(matingId, details) {
		return BreedingManagementAPI.updateMatingStatus(matingId, details);
	},

	/**
	 * Fetches the litter reminder notes for a given mating ID.
	 * 
	 * @param {number} matingId - The ID of the mating.
	 * @param {Object} queryParams - The query parameters for the request.
	 * @returns {Promise} - A promise that resolves to the litter reminder notes.
	 */	
	async getLitterReminderNotes(matingId, queryParams) {
		return BreedingManagementAPI.getLitterReminderNotes(matingId, queryParams)
	},

	async getLitterReminderNotesById(matingId, reminderId) {
		return BreedingManagementAPI.getLitterReminderNotesById(matingId, reminderId)
	},

	/**
	 * @param {*} reminderDetails - Includes the mating ID, reminder type, notes, and reminder date.
	 */
	async saveLitterReminderNotes(reminderDetails) {
        return BreedingManagementAPI.saveLitterReminderNotes(reminderDetails);
	},

	/**
	 * @param {*} reminderDetails - Includes the id, mating ID, reminder type, notes, and reminder date.
	 */
	async updateLitterReminderNotes(reminderDetails) {
        return BreedingManagementAPI.updateLitterReminderNotes(reminderDetails);
	},

	/**
     * Retrieves notes associated with a specific litter.
     *
     * @async
     * @function getLitterNotes
     * @param {number} matingId - The ID of the mating associated with the litter.
     * @param {Object} queryParams - The query parameters for filtering or pagination.
     */
	async getLitterNotes(matingId, queryParams) {
		return BreedingManagementAPI.getLitterNotes(matingId, queryParams)
	},


	/**
     * Saves a new note for a specific litter.
     *
     * @async
     * @function saveLitterNotes
     * @param {Object} noteDetails - The details of the note to be saved.
     * @param {number} noteDetails.matingId - The ID of the mating associated with the litter.
     * @param {string} noteDetails.note - The content of the note to be saved.
     */
	async saveLitterNotes(noteDetails) {
        return BreedingManagementAPI.saveLitterNotes(noteDetails);
	},

	/**
     * Updates an existing note for a specific litter.
     *
     * @async
     * @function updateLitterNotes
     * @param {Object} noteDetails - The details of the note to be updated.
     * @param {number} noteDetails.id - The ID of the note to be updated.
     * @param {number} noteDetails.matingId - The ID of the mating associated with the litter.
     * @param {string} noteDetails.note - The updated content of the note.
     */
	async updateLitterNotes(noteDetails) {
        return BreedingManagementAPI.updateLitterNotes(noteDetails);
	},

	async updateDamWhelpDateAndTypeOfBirth(details) {
		return BreedingManagementAPI.updateDamWhelpDateAndTypeOfBirth(details);
	},


	// *********************************** Waitlist ****************************************************
	/**
	 * Wrapper function that delegates puppy allocation to the BreedingManagementAPI.
	 *
	 * @async
	 * @function allocatePuppy
	 * @param {Object} details - The details for allocating the puppy, passed directly to the API.
	 * @returns {Promise} The result of the allocation request.
	 */
	async allocatePuppy(details) {
		return BreedingManagementAPI.allocatePuppy(details);
	},
		
	async getWaitlist(matingId, queryParams) {
		return BreedingManagementAPI.getWaitlist(matingId, queryParams)
	},

	async deleteAllocatedPuppyById(allocatedPuppyId) {
		return BreedingManagementAPI.deleteAllocatedPuppyById(allocatedPuppyId)
	},

	async getAllocatedPuppyById(allocatedPuppyId) {
		return BreedingManagementAPI.getAllocatedPuppyById(allocatedPuppyId)
	},

	async updateAllocatedPuppy(allocatedPuppyId, details) {
		return BreedingManagementAPI.updateAllocatedPuppy(allocatedPuppyId, details)
	},

	/**
	 * Retrieves the list of puppies from a specific litter that are available for allocation
	 * by delegating the request to the BreedingManagementAPI service.
	 *
	 * @param {string|number} matingId - The ID of the mating record to fetch available puppies for.
	 * @returns {Promise} A promise that resolves to the list of puppies available for allocation.
	 */
	async getPuppiesLitterToAllocate(matingId) {
		return BreedingManagementAPI.getPuppiesLitterToAllocate(matingId)
	},
};

